/**
 * CronScheduleBuilder — human-friendly UI over the raw 5-field cron string.
 *
 * Supports the common schedules that Comms Center flows need:
 *   • Never (manual / on-demand)
 *   • Every N minutes (e.g. every 5 min for marketing sweepers)
 *   • Hourly (at :MM)
 *   • Daily at HH:MM UTC
 *   • Weekly on {day} at HH:MM UTC
 *   • Monthly on day-N at HH:MM UTC
 *   • Custom (raw cron fallback for power users)
 *
 * Cron strings out of range for the presets round-trip to the "Custom" mode
 * automatically so no configuration is lost.
 */
import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Clock } from "lucide-react";

const DAYS = [
  { v: "1", label: "Monday" },
  { v: "2", label: "Tuesday" },
  { v: "3", label: "Wednesday" },
  { v: "4", label: "Thursday" },
  { v: "5", label: "Friday" },
  { v: "6", label: "Saturday" },
  { v: "0", label: "Sunday" },
];

function parseCron(cron) {
  const raw = (cron || "").trim();
  if (!raw || raw.toLowerCase() === "manual" || raw.toLowerCase() === "off") {
    return { mode: "manual" };
  }
  const parts = raw.split(/\s+/);
  if (parts.length !== 5) return { mode: "custom", raw };
  const [m, h, dom, mon, dow] = parts;

  // Every N minutes: */N * * * *
  const everyN = /^\*\/(\d+)$/.exec(m);
  if (everyN && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return { mode: "every_minutes", every_minutes: Number(everyN[1]) };
  }
  // Hourly at :MM
  if (/^\d+$/.test(m) && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return { mode: "hourly", minute: Number(m) };
  }
  // Daily at HH:MM
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && dom === "*" && mon === "*" && dow === "*") {
    return { mode: "daily", hour: Number(h), minute: Number(m) };
  }
  // Weekly at HH:MM on DOW
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && dom === "*" && mon === "*" && /^\d$/.test(dow)) {
    return { mode: "weekly", hour: Number(h), minute: Number(m), dow };
  }
  // Monthly at HH:MM on day-N
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && /^\d+$/.test(dom) && mon === "*" && dow === "*") {
    return { mode: "monthly", hour: Number(h), minute: Number(m), dom: Number(dom) };
  }
  return { mode: "custom", raw };
}

function serializeCron(state) {
  const pad = (n) => String(n).padStart(2, "0");
  const timeParts = (state) => {
    const h = Math.max(0, Math.min(23, Number(state.hour ?? 8)));
    const m = Math.max(0, Math.min(59, Number(state.minute ?? 0)));
    return { h, m };
  };
  switch (state.mode) {
    case "manual": return "manual";
    case "every_minutes": {
      const n = Math.max(1, Math.min(59, Number(state.every_minutes ?? 5)));
      return `*/${n} * * * *`;
    }
    case "hourly": {
      const m = Math.max(0, Math.min(59, Number(state.minute ?? 0)));
      return `${m} * * * *`;
    }
    case "daily": {
      const { h, m } = timeParts(state);
      return `${m} ${h} * * *`;
    }
    case "weekly": {
      const { h, m } = timeParts(state);
      return `${m} ${h} * * ${state.dow ?? "1"}`;
    }
    case "monthly": {
      const { h, m } = timeParts(state);
      const d = Math.max(1, Math.min(28, Number(state.dom ?? 1)));
      return `${m} ${h} ${d} * *`;
    }
    case "custom": return (state.raw || "").trim();
    default: return "manual";
  }
}

function humanize(cron) {
  const s = parseCron(cron);
  const pad = (n) => String(n).padStart(2, "0");
  switch (s.mode) {
    case "manual": return "On-demand only — no schedule.";
    case "every_minutes": return `Every ${s.every_minutes} minute${s.every_minutes === 1 ? "" : "s"}.`;
    case "hourly": return `Every hour at :${pad(s.minute)}.`;
    case "daily": return `Every day at ${pad(s.hour)}:${pad(s.minute)} UTC.`;
    case "weekly": {
      const day = (DAYS.find((d) => d.v === s.dow) || {}).label || "day";
      return `Every ${day} at ${pad(s.hour)}:${pad(s.minute)} UTC.`;
    }
    case "monthly": return `Day ${s.dom} of every month at ${pad(s.hour)}:${pad(s.minute)} UTC.`;
    case "custom": return `Custom cron: ${s.raw}`;
    default: return "";
  }
}

export default function CronScheduleBuilder({ value, onChange, testid = "cron-builder" }) {
  const initial = useMemo(() => parseCron(value), [value]);
  const [state, setState] = useState({
    mode: initial.mode || "manual",
    every_minutes: initial.every_minutes ?? 5,
    hour: initial.hour ?? 8,
    minute: initial.minute ?? 0,
    dow: initial.dow ?? "1",
    dom: initial.dom ?? 1,
    raw: initial.raw ?? (value || ""),
  });
  const [showRaw, setShowRaw] = useState(initial.mode === "custom");

  useEffect(() => {
    const cron = serializeCron(state);
    if (cron !== (value || "")) onChange?.(cron);
  }, [state]);

  // If the parent changes value externally (e.g. flow load), sync.
  useEffect(() => {
    const parsed = parseCron(value);
    setState((s) => ({ ...s, ...parsed }));
    setShowRaw(parsed.mode === "custom");
  }, [value]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Select value={state.mode} onValueChange={(m) => { set("mode", m); setShowRaw(m === "custom"); }}>
            <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-mode`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Never (on-demand)</SelectItem>
              <SelectItem value="every_minutes">Every N minutes</SelectItem>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="custom">Custom cron…</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {state.mode === "every_minutes" && (
          <div>
            <Input type="number" min={1} max={59} value={state.every_minutes}
                   onChange={(e) => set("every_minutes", Number(e.target.value) || 1)}
                   className="h-9 text-xs" data-testid={`${testid}-every-minutes`} />
          </div>
        )}
        {state.mode === "hourly" && (
          <div>
            <Input type="number" min={0} max={59} value={state.minute}
                   onChange={(e) => set("minute", Number(e.target.value) || 0)}
                   placeholder="Minute (0-59)"
                   className="h-9 text-xs" data-testid={`${testid}-hourly-min`} />
          </div>
        )}
        {(state.mode === "daily" || state.mode === "weekly" || state.mode === "monthly") && (
          <div className="grid grid-cols-2 gap-1">
            <Input type="number" min={0} max={23} value={state.hour}
                   onChange={(e) => set("hour", Number(e.target.value) || 0)}
                   className="h-9 text-xs" data-testid={`${testid}-hour`} placeholder="HH" />
            <Input type="number" min={0} max={59} value={state.minute}
                   onChange={(e) => set("minute", Number(e.target.value) || 0)}
                   className="h-9 text-xs" data-testid={`${testid}-minute`} placeholder="MM" />
          </div>
        )}
      </div>
      {state.mode === "weekly" && (
        <div>
          <Select value={state.dow} onValueChange={(v) => set("dow", v)}>
            <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-dow`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {state.mode === "monthly" && (
        <div>
          <Input type="number" min={1} max={28} value={state.dom}
                 onChange={(e) => set("dom", Number(e.target.value) || 1)}
                 className="h-9 text-xs" data-testid={`${testid}-dom`} placeholder="Day of month (1-28)" />
        </div>
      )}
      {(state.mode === "custom" || showRaw) && (
        <div>
          <Input value={state.raw} onChange={(e) => set("raw", e.target.value)}
                 placeholder="min hr day-of-month month day-of-week"
                 className="h-9 font-mono text-xs" data-testid={`${testid}-raw`} />
        </div>
      )}
      <div className="flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1 text-muted-foreground">
          <Clock className="w-3 h-3" /> {humanize(value)}
        </span>
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          className="text-primary hover:underline"
          data-testid={`${testid}-toggle-raw`}
        >
          {showRaw ? "Hide raw cron" : "Show raw cron"}
        </button>
      </div>
    </div>
  );
}

export { parseCron, serializeCron, humanize };
